import React from 'react';
import { Check } from 'lucide-react';
import type { WizardStep } from '../lib/wizardSteps';

interface StepIndicatorProps {
  steps: WizardStep[];
  onStepClick?: (step: WizardStep) => void;
  // false op generatorschermen: niets is klikbaar zolang er gegenereerd wordt.
  interactive?: boolean;
  // Tooltip voor groene stappen die tijdelijk niet klikbaar zijn (bijv. tijdens genereren).
  inactiveReason?: string;
}

// Bolletjes van de blog-wizard. Kleur én klikbaarheid komen uit dezelfde
// WizardStep (zie lib/wizardSteps.ts): groen + klikbaar, groen + vergrendeld
// (met uitleg als tooltip), zwart = huidige stap, grijs = komt nog.
export function StepIndicator({
  steps,
  onStepClick,
  interactive = true,
  inactiveReason = 'Niet beschikbaar tijdens het genereren',
}: StepIndicatorProps) {
  const [hint, setHint] = React.useState<string | null>(null);

  return (
    <nav aria-label="Stappen van de blog-wizard" className="w-full py-6">
      <ol className="flex items-center justify-between max-w-4xl mx-auto">
        {steps.map((step, index) => {
          const clickable = interactive && step.status === 'completed' && step.reachable && !!onStepClick;
          const locked = step.status === 'completed' && !clickable;
          const statusText =
            step.status === 'current' ? 'huidige stap' : step.status === 'completed' ? 'voltooid' : 'komt nog';
          const lockedReason = locked ? (step.lockedReason ?? inactiveReason) : undefined;
          const tooltip = clickable ? `Ga naar stap ${step.number}: ${step.label}` : lockedReason;

          const circleClass =
            step.status === 'current'
              ? 'bg-[#231F20] text-white ring-4 ring-[#231F20]/20 cursor-default'
              : step.status === 'completed'
                ? clickable
                  ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-500/30'
                  : 'bg-green-500/70 text-white cursor-default focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-500/30'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed';

          const labelClass =
            step.status === 'current'
              ? 'text-[#231F20]'
              : step.status === 'completed'
                ? 'text-green-600'
                : 'text-gray-500';

          return (
            <React.Fragment key={step.number}>
              <li className="flex flex-col items-center flex-1">
                <button
                  type="button"
                  onClick={() => {
                    if (clickable) {
                      setHint(null);
                      onStepClick?.(step);
                      return;
                    }
                    if (locked && lockedReason) setHint(lockedReason);
                  }}
                  disabled={step.status === 'upcoming'}
                  aria-disabled={locked || step.status === 'current' || undefined}
                  aria-current={step.status === 'current' ? 'step' : undefined}
                  aria-label={`Stap ${step.number}: ${step.label}, ${statusText}`}
                  title={tooltip}
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-semibold text-base md:text-lg transition-all duration-300 ${circleClass}`}
                >
                  {step.status === 'completed' ? (
                    <Check className="w-5 h-5 md:w-6 md:h-6" aria-hidden="true" />
                  ) : (
                    step.number
                  )}
                </button>
                <div
                  className={`mt-2 text-[10px] md:text-xs font-medium text-center transition-all duration-300 ${labelClass}`}
                >
                  {step.label}
                </div>
              </li>

              {index < steps.length - 1 && (
                <li aria-hidden="true" className="flex-1 h-1 mx-1 md:mx-2 mb-8">
                  <div
                    className={`h-full rounded transition-all duration-300 ${
                      steps[index + 1].status === 'completed' || step.status === 'completed'
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>

      {hint && (
        <p role="status" className="mt-2 text-xs text-gray-600 text-center">
          {hint}
        </p>
      )}
    </nav>
  );
}
